import { useState, useEffect } from 'react';
import type { Listing, CreateListingRequest, UpdateListingRequest } from '../types';
import { createListing, updateListing } from '../api/client';
import { colors } from '../constants/colors';
import { ImageUpload } from './ImageUpload';

interface ListingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (listing: Listing) => void;
  listing?: Listing | null;
}

export function ListingModal({ isOpen, onClose, onSuccess, listing }: ListingModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [startingPrice, setStartingPrice] = useState(10);
  const [stock, setStock] = useState(1);
  const [isUnique, setIsUnique] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = !!listing;
  const canEdit = !listing || listing.canEdit;

  useEffect(() => {
    if (listing) {
      setName(listing.name);
      setDescription(listing.description || '');
      setImageUrl(listing.imageUrl || '');
      setStartingPrice(listing.startingPrice);
      setStock(listing.stock);
      setIsUnique(listing.isUnique);
    } else {
      setName('');
      setDescription('');
      setImageUrl('');
      setStartingPrice(10);
      setStock(1);
      setIsUnique(false);
    }
    setError(null);
  }, [listing, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let result: Listing;
      if (isEditMode && listing) {
        const request: UpdateListingRequest = {
          name,
          description: description || undefined,
          imageUrl: imageUrl || undefined,
          startingPrice,
          stock,
          isUnique,
        };
        result = await updateListing(listing.id, request);
      } else {
        const request: CreateListingRequest = {
          name,
          description: description || undefined,
          imageUrl: imageUrl || undefined,
          startingPrice,
          stock,
          isUnique,
        };
        result = await createListing(request);
      }
      onSuccess(result);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save listing');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>{isEditMode ? 'Edit Listing' : 'Add Listing'}</h2>
          <button onClick={onClose} style={styles.closeButton}>
            <CloseIcon />
          </button>
        </div>

        {!canEdit && (
          <div style={styles.warning}>
            This listing cannot be edited while items are being auctioned.
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={styles.input}
              placeholder="Enter product name"
              disabled={!canEdit}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={styles.textarea}
              placeholder="Enter product description"
              disabled={!canEdit}
            />
          </div>

          <div style={styles.formGroup}>
            <ImageUpload
              value={imageUrl || null}
              onChange={(url) => setImageUrl(url || '')}
              folder="listings"
              label="Image"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={isUnique}
                onChange={(e) => {
                  setIsUnique(e.target.checked);
                  if (e.target.checked) setStock(1);
                }}
                style={styles.checkbox}
                disabled={!canEdit}
              />
              <span>Unique item</span>
            </label>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Starting Price</label>
              <div style={styles.priceInput}>
                <span style={styles.pricePrefix}>$</span>
                <input
                  type="number"
                  value={startingPrice}
                  onChange={(e) => setStartingPrice(Number(e.target.value))}
                  style={styles.inputWithPrefix}
                  min={0}
                  step={0.01}
                  disabled={!canEdit}
                />
              </div>
            </div>

            {!isUnique && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Stock</label>
                <input
                  type="number"
                  value={stock}
                  onChange={(e) => setStock(Number(e.target.value))}
                  style={styles.input}
                  min={1}
                  disabled={!canEdit}
                />
              </div>
            )}
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.actions}>
            <button type="button" onClick={onClose} style={styles.cancelButton}>
              Cancel
            </button>
            <button
              type="submit"
              style={styles.submitButton}
              disabled={loading || !canEdit}
            >
              {loading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${colors.gray[300]}`,
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    margin: 0,
    color: colors.ink.black,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: colors.gray[600],
    padding: '4px',
  },
  warning: {
    backgroundColor: '#FEF3C7',
    border: '1px solid #F59E0B',
    color: '#92400E',
    padding: '12px 24px',
    fontSize: '14px',
  },
  form: {
    padding: '24px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[900],
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '8px',
    fontSize: '14px',
    color: colors.ink.black,
    backgroundColor: colors.white,
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '8px',
    fontSize: '14px',
    color: colors.ink.black,
    backgroundColor: colors.white,
    minHeight: '80px',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  priceInput: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '8px',
    overflow: 'hidden',
  },
  pricePrefix: {
    padding: '10px 12px',
    backgroundColor: colors.gray[100],
    color: colors.gray[600],
    fontSize: '14px',
  },
  inputWithPrefix: {
    flex: 1,
    padding: '10px 12px',
    border: 'none',
    fontSize: '14px',
    color: colors.ink.black,
    backgroundColor: colors.white,
  },
  imagePreview: {
    marginTop: '8px',
  },
  previewImage: {
    width: '80px',
    height: '80px',
    objectFit: 'cover',
    borderRadius: '8px',
    border: `1px solid ${colors.gray[300]}`,
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: colors.ink.black,
    cursor: 'pointer',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  hint: {
    color: colors.gray[600],
    fontSize: '12px',
    marginLeft: '4px',
  },
  error: {
    backgroundColor: `${colors.accent.pink}15`,
    border: `1px solid ${colors.accent.pink}40`,
    borderRadius: '8px',
    padding: '12px',
    color: colors.accent.pink,
    fontSize: '14px',
    marginBottom: '16px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: colors.gray[100],
    color: colors.gray[900],
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  submitButton: {
    padding: '10px 20px',
    backgroundColor: colors.brand.blue,
    color: colors.white,
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};
